/**
 * Client-side preview of an inquiry's money figures.
 *
 * MIRRORS app/models/inquiry.py on the backend — specifically
 * calculate_items_subtotal / calculate_items_gst / _effective_freight /
 * calculate_freight_gst / calculate_loading_cost_gst / calculate_total.
 * If you change one side, change the other. The view screen renders
 * BACKEND-computed values, so any drift here shows up as the numbers moving
 * the moment an inquiry is saved.
 *
 * Freight is not a simple addition — the type decides the sign:
 *   ex_warehouse                   no billing effect
 *   free_delivery_by_supplier      no billing effect
 *   free_delivery_paid_by_company  no billing effect
 *   freight_chargeable             ADDS freight + 18% GST
 *   free_delivery_freight_to_pay   SUBTRACTS freight (the supplier pays it)
 * and when freight_pricing_type is 'per_kg' the freight field is a RATE that
 * multiplies total weight rather than a fixed amount.
 */
import { lineWeightKg } from './lineUnits';

/** GST on freight and on loading cost is a flat 18%, per the backend. */
export const ANCILLARY_GST_RATE = 0.18;

export const FREIGHT_CHARGEABLE = 'freight_chargeable';
export const FREE_DELIVERY_FREIGHT_TO_PAY = 'free_delivery_freight_to_pay';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** price x quantity for one line. Unit-agnostic, exactly like the backend. */
export const lineSubtotal = (item) => num(item?.quantity) * num(item?.price);

/**
 * GST for one line. Mirrors InquiryItem.calculate_gst_amount: when the price
 * already includes GST, or the product has no rate, the line contributes zero.
 */
export const lineGst = (item, includeGst) => {
  const rate = num(item?.gst_rate);
  if (includeGst || rate <= 0) return 0;
  return lineSubtotal(item) * (rate / 100);
};

/**
 * Total weight in kg across all lines — only used when freight is priced
 * per kg. Uses the same ladder as everything else so a tonne line counts as
 * 1000 kg rather than 1.
 */
export const totalWeightKg = (items = [], products = []) =>
  items.reduce((sum, it) => {
    if (!it?.product_size_id) return sum;
    const p = products.find((x) => String(x.id) === String(it.product_size_id));
    return sum + lineWeightKg({
      unit: it.units,
      quantity: it.quantity,
      length: it.length,
      productWeight: p?.product_weight,
      standardLength: p?.standard_length,
    });
  }, 0);

/**
 * Every figure the summary panel needs.
 *
 * @param {object} order    the form state (items, include_gst, freight,
 *                          freight_type, freight_pricing_type, loading_cost_per_kg)
 * @param {Array}  products loaded product sizes, for the per-kg weight basis
 */
export const computeInquiryTotals = (order = {}, products = []) => {
  const items = (order.items || []).filter((it) => it?.product_size_id);
  const includeGst = Boolean(order.include_gst);

  const subtotal = items.reduce((s, it) => s + lineSubtotal(it), 0);
  const itemsGst = items.reduce((s, it) => s + lineGst(it, includeGst), 0);

  const freightType = order.freight_type || FREIGHT_CHARGEABLE;
  const freightRaw = num(order.freight);
  const effectiveFreight =
    freightType === FREIGHT_CHARGEABLE && order.freight_pricing_type === 'per_kg'
      ? freightRaw * totalWeightKg(items, products)
      : freightRaw;

  // Only chargeable freight attracts GST.
  const freightGst =
    freightType === FREIGHT_CHARGEABLE ? effectiveFreight * ANCILLARY_GST_RATE : 0;

  const loadingCost = num(order.loading_cost_per_kg);
  const loadingCostGst = loadingCost ? loadingCost * ANCILLARY_GST_RATE : 0;

  let total = subtotal + itemsGst;
  if (freightType === FREIGHT_CHARGEABLE) {
    total += effectiveFreight + freightGst;
  } else if (freightType === FREE_DELIVERY_FREIGHT_TO_PAY) {
    total -= effectiveFreight;
  }
  if (loadingCost) {
    total += loadingCost + loadingCostGst;
  }

  return {
    subtotal,
    gstAmount: itemsGst,
    freight: effectiveFreight,
    freightGst,
    loadingCost,
    loadingCostGst,
    total,
    /** True when a line has no GST rate yet, so the figure is provisional. */
    hasUnknownGstRate: !includeGst && items.some((it) => num(it?.gst_rate) <= 0),
  };
};
